<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount, getCurrentInstance } from "vue";
import "firebase/functions";
import firebase from "firebase/app";
import Shell from "./Shell.vue";
import {
  entityNumberAtCaret,
  formatMarker,
  parseModel,
  typeLabel,
} from "./entityText.mjs";
import { searchDatabaseItems } from "./databaseSearch.mjs";

const props = defineProps({
  modelValue: { type: String, default: "" },
  label: String,
  tooltip: String,
  name: String,
  placeholder: { type: String, default: "" },
  disabled: { type: Boolean, default: false },
  required: { type: Boolean, default: false },
  bare: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  submitOnEnter: { type: Boolean, default: false },
  suggestionsAbove: { type: Boolean, default: false },
  pillIcon: { type: Function, default: null },
});

const emit = defineEmits(["update:modelValue", "submit"]);

const Bare = (_, { slots }) => slots.comp?.();
const Wrapper = computed(() => (props.bare ? Bare : Shell));

const editor = ref(null);
let debounceTimer = null;
let lastEmitted = props.modelValue || "";
let requestId = 0;
let activeMatch = null;
let composing = false;
const suggestions = ref([]);
const selectedIndex = ref(0);
const searching = ref(false);
const lookupError = ref("");
const listId = `entity-text-options-${getCurrentInstance().uid}`;

function databaseURL() {
  try {
    const raw = import.meta.env?.VITE_APP_CONFIG;
    if (!raw) return undefined;
    return JSON.parse(raw).firebaseConfig?.databaseURL;
  } catch {
    return undefined;
  }
}

function createPill(segment) {
  const pill = document.createElement("span");
  pill.className = "entity-pill";
  pill.contentEditable = "false";
  pill.dataset.entity = "pill";
  pill.dataset.type = segment.entityType;
  pill.dataset.number = segment.number;
  pill.dataset.id = segment.entityId;
  pill.dataset.title = segment.title || "";

  const number = document.createElement("span");
  number.className = "entity-pill-number";
  number.textContent = segment.number;

  const meta = document.createElement("span");
  meta.className = "entity-pill-meta";
  const title = segment.title ? ` · ${segment.title}` : "";
  meta.textContent = `${typeLabel(segment.entityType)}${title}`;

  const remove = document.createElement("button");
  remove.type = "button";
  remove.className = "entity-pill-remove";
  remove.setAttribute("aria-label", `Remove ${segment.number}`);
  remove.textContent = "×";
  remove.addEventListener("mousedown", (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (props.disabled) return;
    closeSuggestions();
    const text = document.createTextNode(segment.number);
    pill.replaceWith(text);
    placeCaret(text, text.length);
    const value = serialize(editor.value);
    lastEmitted = value;
    emit("update:modelValue", value);
  });

  const icon = props.pillIcon?.(segment);
  if (icon instanceof Node) {
    const badge = document.createElement("span");
    badge.className = "entity-pill-icon";
    badge.appendChild(icon);
    pill.append(badge);
  }
  pill.append(number, meta, remove);
  return pill;
}

function appendText(parent, text) {
  const lines = String(text).split("\n");
  lines.forEach((line, index) => {
    if (index > 0) parent.appendChild(document.createElement("br"));
    if (line) parent.appendChild(document.createTextNode(line));
  });
}

function render(value) {
  const root = editor.value;
  if (!root) return;
  root.replaceChildren();
  for (const segment of parseModel(value)) {
    if (segment.type === "text") appendText(root, segment.text);
    else root.appendChild(createPill(segment));
  }
}

function serialize(root) {
  let out = "";
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += node.textContent;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (node.dataset?.entity === "pill") {
      out += formatMarker({
        entityType: node.dataset.type,
        number: node.dataset.number,
        entityId: node.dataset.id,
        title: node.dataset.title,
      });
      return;
    }
    if (node.tagName === "BR") {
      out += "\n";
      return;
    }
    const block = node.tagName === "DIV" || node.tagName === "P";
    if (block && out && !out.endsWith("\n")) out += "\n";
    for (const child of node.childNodes) walk(child);
  };
  for (const child of root.childNodes) walk(child);
  return out;
}

function publish() {
  const value = serialize(editor.value);
  lastEmitted = value;
  emit("update:modelValue", value);
  scheduleResolve();
}

function onInput() {
  publish();
}

function closeSuggestions() {
  clearTimeout(debounceTimer);
  requestId += 1;
  activeMatch = null;
  suggestions.value = [];
  searching.value = false;
  lookupError.value = "";
  selectedIndex.value = 0;
}

function matchAtCaret() {
  const root = editor.value;
  const selection = window.getSelection();
  if (!root || props.disabled || composing || document.activeElement !== root ||
      !selection?.isCollapsed || selection.anchorNode?.nodeType !== Node.TEXT_NODE) return null;
  const node = selection.anchorNode;
  if (!root.contains(node) || node.parentElement?.closest('[data-entity="pill"]')) return null;
  const text = node.textContent || "";
  const match = entityNumberAtCaret(text, selection.anchorOffset);
  return match ? { node, text, match } : null;
}

function scheduleResolve() {
  const candidate = matchAtCaret();
  if (candidate && activeMatch?.node === candidate.node &&
      activeMatch.text === candidate.text && activeMatch.match.start === candidate.match.start) return;
  closeSuggestions();
  if (!candidate) return;
  activeMatch = candidate;
  searching.value = true;
  const current = requestId;
  debounceTimer = setTimeout(() => resolveMatches(candidate, current), 300);
}

function placeCaret(node, offset) {
  const selection = window.getSelection();
  if (!selection) return;
  const range = document.createRange();
  range.setStart(node, offset);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

function replaceWithPill(node, match, hit) {
  const text = node.textContent || "";
  const before = text.slice(0, match.start);
  const after = text.slice(match.end);
  const selection = window.getSelection();
  const caretInNode = selection && selection.rangeCount > 0 && selection.anchorNode === node;
  const caretOffset = caretInNode ? selection.anchorOffset : null;
  const pill = createPill({
    entityType: hit.type,
    number: hit.number || match.number,
    entityId: hit.entityId,
    title: hit.title || "",
  });
  const fragment = document.createDocumentFragment();
  const beforeNode = before ? document.createTextNode(before) : null;
  const afterNode = after ? document.createTextNode(after) : null;
  if (beforeNode) fragment.appendChild(beforeNode);
  fragment.appendChild(pill);
  if (afterNode) fragment.appendChild(afterNode);
  node.parentNode.replaceChild(fragment, node);

  if (!caretInNode || caretOffset == null) return;
  if (caretOffset <= match.start && beforeNode) {
    placeCaret(beforeNode, Math.min(caretOffset, beforeNode.textContent.length));
  } else if (caretOffset >= match.end && afterNode) {
    placeCaret(afterNode, Math.max(0, caretOffset - match.end));
  } else if (afterNode) {
    placeCaret(afterNode, 0);
  } else {
    placeCaret(pill.parentNode, Array.prototype.indexOf.call(pill.parentNode.childNodes, pill) + 1);
  }
}

async function resolveMatches(candidate, current) {
  try {
    const resolve = firebase.app().functions().httpsCallable("resolveDatabaseItems");
    const matches = await searchDatabaseItems(candidate.match, {
      resolve,
      databaseURL: databaseURL(),
    });
    if (current !== requestId) return;
    suggestions.value = matches;
  } catch (err) {
    if (current !== requestId) return;
    console.error("Entity resolve failed:", err);
    lookupError.value = "Could not look up matching entities. You can continue typing.";
  } finally {
    if (current === requestId) searching.value = false;
  }
}

function selectSuggestion(hit) {
  const candidate = activeMatch;
  if (props.disabled || !candidate || !editor.value?.contains(candidate.node) ||
      candidate.node.parentElement?.closest('[data-entity="pill"]') ||
      candidate.node.textContent !== candidate.text) {
    closeSuggestions();
    return;
  }
  closeSuggestions();
  replaceWithPill(candidate.node, candidate.match, hit);
  lastEmitted = serialize(editor.value);
  emit("update:modelValue", lastEmitted);
}

function onKeydown(event) {
  if (event.key === "Enter") event.stopPropagation();
  if (event.key === "Escape") {
    closeSuggestions();
  } else if (suggestions.value.length) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      selectedIndex.value = (selectedIndex.value + step + suggestions.value.length) % suggestions.value.length;
    } else if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      selectSuggestion(suggestions.value[selectedIndex.value]);
    }
  } else if (props.submitOnEnter && event.key === "Enter" && !event.shiftKey && !event.isComposing && !composing) {
    event.preventDefault();
    closeSuggestions();
    emit("submit");
  }
}

function onCompositionStart() {
  composing = true;
  closeSuggestions();
}

function onCompositionEnd() {
  composing = false;
  publish();
}

onMounted(() => {
  render(props.modelValue || "");
  document.addEventListener("selectionchange", scheduleResolve);
});

onBeforeUnmount(() => {
  closeSuggestions();
  document.removeEventListener("selectionchange", scheduleResolve);
});

watch(() => props.disabled, closeSuggestions);

watch(
  () => props.modelValue,
  (value) => {
    if ((value || "") === lastEmitted) return;
    closeSuggestions();
    lastEmitted = value || "";
    render(lastEmitted);
  }
);

defineExpose({ inputElement: editor });
</script>

<template>
  <component :is="Wrapper" :label="label" :tooltip="tooltip">
    <template #comp>
      <div class="entity-text-wrap">
        <div
          ref="editor"
          class="entity-text"
          :class="{ 'entity-text-compact': compact }"
          :contenteditable="disabled ? 'false' : 'true'"
          :data-placeholder="placeholder"
          role="combobox"
          :aria-label="label || name || 'Entity text'"
          aria-multiline="true"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          :aria-expanded="suggestions.length > 0"
          :aria-controls="suggestions.length ? listId : undefined"
          :aria-activedescendant="suggestions.length ? `${listId}-${selectedIndex}` : undefined"
          :aria-disabled="disabled"
          @input="onInput"
          @keydown="onKeydown"
          @blur="closeSuggestions"
          @compositionstart="onCompositionStart"
          @compositionend="onCompositionEnd"
        ></div>
        <div
          v-if="suggestions.length"
          :id="listId"
          class="entity-suggestions"
          :class="{ 'entity-suggestions-above': suggestionsAbove }"
          role="listbox"
          aria-label="Matching entities"
        >
          <button
            v-for="(hit, index) in suggestions"
            :id="`${listId}-${index}`"
            :key="`${hit.type}:${hit.entityId}`"
            type="button"
            role="option"
            tabindex="-1"
            :aria-selected="index === selectedIndex"
            class="entity-suggestion"
            :class="{ 'entity-suggestion-active': index === selectedIndex }"
            @mousedown.prevent
            @click="selectSuggestion(hit)"
          >
            <strong>{{ hit.number }}</strong>
            <span>{{ typeLabel(hit.type) }}{{ hit.title ? ` · ${hit.title}` : "" }}</span>
          </button>
        </div>
        <p v-if="searching" class="text-sm text-gray-600" role="status">Looking up matching entities...</p>
        <p v-if="lookupError" class="text-sm text-red-700" role="alert">{{ lookupError }}</p>
        <input
          class="entity-text-value"
          type="text"
          tabindex="-1"
          :name="name"
          :value="modelValue || ''"
          :required="required"
        />
      </div>
    </template>
  </component>
</template>

<style scoped>
.entity-text-wrap {
  position: relative;
  width: 100%;
}

.entity-text {
  min-height: 96px;
  width: 100%;
  white-space: pre-wrap;
  outline: none;
  line-height: 1.7;
  font-size: 0.875rem;
}

.entity-text-compact {
  min-height: 2.5rem;
  max-height: 8rem;
  overflow-y: auto;
  padding: 0.5rem 0.25rem;
}

.entity-text:empty::before {
  content: attr(data-placeholder);
  color: #9ca3af;
}

.entity-text-value {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  height: 0;
  width: 0;
}

.entity-suggestions {
  position: absolute;
  z-index: 10;
  width: 100%;
  max-height: 15rem;
  overflow-y: auto;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.25rem;
  box-shadow: 0 4px 6px rgb(0 0 0 / 10%);
}

.entity-suggestions-above {
  bottom: 100%;
  margin-bottom: 0.25rem;
}

.entity-suggestion {
  display: flex;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem;
  text-align: left;
  font-size: 0.875rem;
}

.entity-suggestion:hover,
.entity-suggestion-active {
  background: #f3f4f6;
}

:deep(.entity-pill) {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0 0.15rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  background: #e5e7eb;
  line-height: 1.5rem;
  vertical-align: baseline;
}

:deep(.entity-pill-icon) {
  display: grid;
  place-items: center;
  width: 1.2rem;
  height: 1.2rem;
  margin-left: -0.25rem;
  border-radius: 999px;
  background: white;
  box-shadow: 0 1px 2px rgb(0 0 0 / 15%);
  color: var(--entity-pill-icon-color, #374151);
}

:deep(.entity-pill-icon svg) {
  width: 0.65rem;
  height: 0.65rem;
}

:deep(.entity-pill-number) {
  font-weight: 600;
}

:deep(.entity-pill-meta) {
  color: #4b5563;
}

:deep(.entity-pill-remove) {
  border: 0;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}
</style>
